import { GetRowIdParams, GridOptions } from 'ag-grid-community';
import { keyBy } from 'lodash';
import { pipe } from 'ramda';
import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import {
  Grid,
  withEditingMask,
  withExternalFilter,
  withSingleClickEdit,
} from 'components/atoms/grid';
import { PageLoading } from 'components/atoms/loading';
import { withStylePreferenceProvider } from 'components/atoms/style-preference-context';
import {
  Email,
  GetUserName,
  MoreActionsCellRenderer,
  Status,
  getPermission,
} from 'components/organisms/module-settings/team-members/list/table-components';
import { User } from 'data/members/api';
import { useGrid } from 'hooks/use-grid';
import { useTranslate } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { useExternalTeamMemberGridToUpdate } from 'queries/members';
import { useCustomerTeamMemberFieldsAsFilterableFields } from 'utils/team-member-fields/customer-team-member-fields';
import {
  statusField,
  useUserFieldsAsFilterableFields,
  userEmailIdField,
} from 'utils/user-fields';
import {
  relativelySortByDefaultColumnOrder,
  toColDef,
  transformMemberData,
  withMoreActionsColumn,
  withOnUpdate,
} from './columns';

// const GridWrapper = styled.div`
//   height: 400;
//   width: '100%';
//   overflow: 'scroll';
// `;

export const StyledGrid = styled(Grid)`
  width: 100%;
  .ag-header {
    z-index: 0;
  }
  .ag-row {
    border-bottom: 1px solid var(--table-border-color);
  }
  .ag-overlay-no-rows-wrapper {
    display: flex;
    text-align: unset;
    pointer-events: auto;
  }
  .ag-pinned-left-cols-container {
    padding: 0 !important;
  }
  .ag-cell-last-left-pinned > .ag-react-container {
    align-self: stretch;
    width: 100%;
    > div {
      width: 100%;
    }
  }

  .ag-center-cols-container {
    .ag-row {
      .ag-cell:first-child {
        cursor: default;
        .ag-react-container {
          justify-content: space-between;
          align-items: center;
          width: 100%;
          height: 100%;
        }
      }
    }
  }
`;

export const TeamMembersListGrid = pipe(
  pipe(
    () => StyledGrid,
    withExternalFilter,
    withSingleClickEdit,
    withStylePreferenceProvider({ size: 'large' }),
    withEditingMask,
  ),
)();

export const getDefaultOptions: GridOptions = {
  defaultColDef: {
    resizable: true,
    sortable: true,
    editable: false,
    lockPinned: true,
    suppressMovable: false,
    suppressMenu: true,
    cellEditorPopup: true,
    cellEditorPopupPosition: 'under',
  },
  headerHeight: 32,
  rowHeight: 48,
  unSortIcon: true,
  suppressContextMenu: true,
  suppressRowClickSelection: true,
  suppressCellFocus: true,
  animateRows: true,
  debounceVerticalScrollbar: true,
  suppressAnimationFrame: true,
  noRowsOverlayComponent: 'NoResultTeamMemberEmptyState',
  components: {
    email: Email,
    GetUserName,
    getPermission,
    MoreActionsCellRenderer,
    Status,
  },
  getRowId: (d: GetRowIdParams) => {
    return (d.data?.userId ?? d.data?.emailId)?.toString?.();
  },
};

export const ExternalTeamMemberList = ({
  project,
  memberships,
  loggedInUserPermissions,
  memberData,
  membersMeta,
}) => {
  const loggedInUser = useUser();
  const { fields: userFields, isFieldsLoading } =
    useUserFieldsAsFilterableFields({ hidePartnerField: true });
  const { fields: teamMemberFields, loading } =
    useCustomerTeamMemberFieldsAsFilterableFields();

  const columnFields = useMemo(() => {
    return isFieldsLoading || loading
      ? []
      : [
          ...userFields.filter(({ fieldName }) => {
            return ['userId', '', '', ''].includes(fieldName);
          }),
          userEmailIdField,
          statusField,
          ...teamMemberFields,
        ];
  }, [isFieldsLoading, loading, teamMemberFields, userFields]);

  const { gridApi, onGridReady } = useGrid();

  const translate = useTranslate();

  const { mutateAsync } = useExternalTeamMemberGridToUpdate(project.projectId);

  useEffect(() => {
    if (!gridApi) {
      return;
    }
    const toColumnDefs = pipe(
      toColDef(translate, false, true, memberships),
      withMoreActionsColumn(
        project,
        loggedInUserPermissions,
        membersMeta,
        loggedInUser,
        memberships,
      ),
      withOnUpdate(mutateAsync as unknown as any),
      relativelySortByDefaultColumnOrder,
    );

    const columnDefs = toColumnDefs(columnFields);

    const columnFieldLookupById = keyBy(columnFields, 'fieldId');

    gridApi?.setColumnDefs(columnDefs);

    const rowData = transformMemberData(
      columnFieldLookupById,
      memberData as User[],
    );

    gridApi?.setRowData(rowData);
    gridApi?.sizeColumnsToFit();
    gridApi.refreshClientSideRowModel();
  }, [
    columnFields,
    gridApi,
    loggedInUser,
    loggedInUserPermissions,
    memberData,
    membersMeta,
    memberships,
    mutateAsync,
    project,
    translate,
  ]);

  const gridHeight = useMemo(() => {
    const rowHeight = 47;
    const headerHeight = 36;
    const emptyWrapperHeight = 180;

    if ((memberData?.length ?? 0) === 0) {
      return emptyWrapperHeight;
    }

    return memberData.length * rowHeight + headerHeight;
  }, [memberData.length]);

  return isFieldsLoading ? (
    <PageLoading />
  ) : (
    <div
      className="ag-theme-balham"
      style={{
        height: gridHeight,
        width: '100%',
        overflow: 'scroll',
      }}
    >
      <TeamMembersListGrid
        {...getDefaultOptions}
        onGridReady={onGridReady}
        size="large"
      />
    </div>
  );
};
